import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Languages, RotateCcw, Save, Trash2, Search, AlertCircle, Sparkles } from 'lucide-react';
import i18n, { applyOverrides } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only editor for the runtime translation dictionary.
 *
 * Keys still ship as ``frontend/src/i18n/{en,pl}.json`` defaults. Any edit
 * here creates an *override* record in Mongo that layers on top of the
 * static bundle. Deleting an override reverts that key to its static
 * default without touching the JSON file.
 */
const TranslationsSettings = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');   // all | overridden | missing_pl
  const [drafts, setDrafts] = useState({});      // {key: {en?, pl?}}
  const [saving, setSaving] = useState({});      // {key-lang: true}
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);

  const loadCatalogue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/i18n/catalogue`);
      setRows(res.data.keys || []);
      setDrafts({});
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'overridden' && !(r.en_override || r.pl_override)) return false;
      // "Missing PL" = neither the static default nor a saved override has content.
      if (filter === 'missing_pl') {
        const plHasContent = (r.pl_default || '').trim() || (r.pl_override || '').trim() || (r.pl || '').trim();
        if (plHasContent) return false;
      }
      if (!term) return true;
      return (
        r.key.toLowerCase().includes(term) ||
        (r.en || '').toLowerCase().includes(term) ||
        (r.pl || '').toLowerCase().includes(term)
      );
    });
  }, [rows, search, filter]);

  const isRowMissingPl = (r) => (
    !(r.pl_default || '').trim() && !(r.pl_override || '').trim() && !(r.pl || '').trim()
  );

  const overriddenCount = rows.filter((r) => r.en_override || r.pl_override).length;
  const missingPlCount = rows.filter(isRowMissingPl).length;

  const setDraft = (key, lang, value) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [lang]: value } }));
  };

  const clearDraft = (key, lang) => {
    setDrafts((prev) => {
      const next = { ...prev };
      if (next[key]) {
        const { [lang]: _, ...rest } = next[key];
        next[key] = rest;
        if (!Object.keys(next[key]).length) delete next[key];
      }
      return next;
    });
  };

  const saveOne = async (row, lang) => {
    const draft = drafts[row.key]?.[lang];
    if (draft === undefined) return;
    const flag = `${row.key}-${lang}`;
    setSaving((prev) => ({ ...prev, [flag]: true }));
    try {
      await axios.put(`${API}/i18n/overrides/${lang}`, { key: row.key, value: draft });
      i18n.addResource(lang, 'translation', row.key, draft);
      setRows((prev) => prev.map((r) => {
        if (r.key !== row.key) return r;
        const isDefault = (lang === 'en' ? r.en_default : r.pl_default) === draft;
        return {
          ...r,
          [lang]: draft,
          [`${lang}_override`]: isDefault ? null : draft,
        };
      }));
      clearDraft(row.key, lang);
      toast.success(t('translationsEditor.toasts.saved', { key: row.key, lang: lang.toUpperCase() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.saveFailed'));
    } finally {
      setSaving((prev) => ({ ...prev, [flag]: false }));
    }
  };

  const resetOne = async (row, lang) => {
    if (!row[`${lang}_override`]) return;
    const flag = `${row.key}-${lang}`;
    setSaving((prev) => ({ ...prev, [flag]: true }));
    try {
      await axios.delete(`${API}/i18n/overrides/${lang}/${encodeURIComponent(row.key)}`);
      const defaultValue = lang === 'en' ? row.en_default : row.pl_default;
      i18n.addResource(lang, 'translation', row.key, defaultValue);
      setRows((prev) => prev.map((r) => (
        r.key !== row.key ? r : { ...r, [lang]: defaultValue, [`${lang}_override`]: null }
      )));
      clearDraft(row.key, lang);
      toast.success(t('translationsEditor.toasts.reverted', { key: row.key, lang: lang.toUpperCase() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.resetFailed'));
    } finally {
      setSaving((prev) => ({ ...prev, [flag]: false }));
    }
  };

  const resetAll = async () => {
    setConfirmResetAll(false);
    try {
      await axios.delete(`${API}/i18n/overrides`);
      applyOverrides({ en: {}, pl: {} });
      toast.success(t('translationsEditor.toasts.resetAllDone'));
      await loadCatalogue();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.resetFailed'));
    }
  };

  /** Auto-translate every key with a missing Polish default via LLM.
   *  Runs in chunks of 50 so a single failure doesn't kill the batch. */
  const autoTranslateMissing = async () => {
    const missing = rows.filter(isRowMissingPl);
    if (missing.length === 0) {
      toast.info(t('translationsEditor.toasts.autoTranslateNothing'));
      return;
    }
    setAutoTranslating(true);
    const toastId = toast.loading(t('translationsEditor.toasts.autoTranslateStart', { count: missing.length }));
    let totalTranslated = 0;
    let totalSkipped = 0;
    let failedChunks = 0;
    const CHUNK = 50;
    for (let i = 0; i < missing.length; i += CHUNK) {
      const slice = missing.slice(i, i + CHUNK);
      const items = slice.map((r) => ({ key: r.key, source: r.en || r.en_default }));
      try {
        const res = await axios.post(`${API}/i18n/translate-batch`, {
          target_lang: 'pl',
          source_lang: 'en',
          items,
        });
        const translations = res.data.translations || {};
        if (Object.keys(translations).length) {
          await axios.post(`${API}/i18n/overrides/pl/bulk`, { overrides: translations });
          Object.entries(translations).forEach(([k, v]) => {
            i18n.addResource('pl', 'translation', k, v);
          });
        }
        totalTranslated += Object.keys(translations).length;
        totalSkipped += (res.data.skipped || []).length;
      } catch (err) {
        failedChunks += 1;
        totalSkipped += slice.length;
        // Keep going — one bad chunk shouldn't kill the whole batch.
      }
    }
    setAutoTranslating(false);
    if (totalTranslated > 0) {
      toast.success(
        t('translationsEditor.toasts.autoTranslateDone', { count: totalTranslated, skipped: totalSkipped }),
        { id: toastId },
      );
    } else {
      toast.error(t('translationsEditor.toasts.autoTranslateFailed'), { id: toastId });
    }
    // Non-blocking reload so counts + row states reflect the persisted overrides.
    loadCatalogue().catch(() => {});
    if (failedChunks > 0) {
      // Surface partial failure separately so the operator knows to retry.
      toast.warning(`${failedChunks} chunk(s) failed — retry to fill the rest.`);
    }
  };

  return (
    <Card data-testid="translations-settings-card">
      <CardHeader>
        <div className="flex flex-wrap gap-3 items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t('translationsEditor.title')}
            </CardTitle>
            <CardDescription>{t('translationsEditor.description')}</CardDescription>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Badge variant="outline" data-testid="translations-count-overridden">
              {t('translationsEditor.overridden', { count: overriddenCount })}
            </Badge>
            <Badge variant="outline" data-testid="translations-count-missing-pl">
              {t('translationsEditor.missingPl', { count: missingPlCount })}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={autoTranslateMissing}
              disabled={autoTranslating || missingPlCount === 0}
              title={t('translationsEditor.autoTranslateHint')}
              data-testid="translations-auto-translate"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              {autoTranslating ? t('translationsEditor.translating') : t('translationsEditor.autoTranslate')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmResetAll(true)}
              disabled={overriddenCount === 0}
              data-testid="translations-reset-all"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('translationsEditor.resetAll')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('translationsEditor.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="translations-search-input"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[220px]" data-testid="translations-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('translationsEditor.filter.all')}</SelectItem>
              <SelectItem value="overridden">{t('translationsEditor.filter.overridden')}</SelectItem>
              <SelectItem value="missing_pl">{t('translationsEditor.filter.missingPl')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">{t('translationsEditor.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">{t('translationsEditor.empty')}</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                    <th className="px-3 py-2 w-1/4">{t('translationsEditor.columns.key')}</th>
                    <th className="px-3 py-2 w-1/3">{t('translationsEditor.columns.english')}</th>
                    <th className="px-3 py-2 w-1/3">{t('translationsEditor.columns.polski')}</th>
                    <th className="px-3 py-2 w-24 text-right">{t('translationsEditor.columns.reset')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 500).map((row) => {
                    const enVal = drafts[row.key]?.en ?? row.en ?? '';
                    const plVal = drafts[row.key]?.pl ?? row.pl ?? '';
                    const enDirty = drafts[row.key]?.en !== undefined && drafts[row.key].en !== row.en;
                    const plDirty = drafts[row.key]?.pl !== undefined && drafts[row.key].pl !== row.pl;
                    const missing = isRowMissingPl(row);
                    return (
                      <tr key={row.key} className="border-b hover:bg-slate-50/50" data-testid={`translation-row-${row.key}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700 align-top break-all">
                          <div className="flex items-start gap-1">
                            <span>{row.key}</span>
                            {missing && <AlertCircle className="h-3 w-3 text-amber-500 flex-none mt-0.5" title={t('translationsEditor.missingHint')} />}
                          </div>
                          {(row.en_override || row.pl_override) && (
                            <div className="text-[10px] text-emerald-600 mt-1">{t('translationsEditor.edited')}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex gap-1">
                            <Input
                              value={enVal}
                              onChange={(e) => setDraft(row.key, 'en', e.target.value)}
                              className={`text-sm ${enDirty ? 'border-amber-500' : ''} ${row.en_override ? 'bg-emerald-50' : ''}`}
                              data-testid={`translation-en-${row.key}`}
                            />
                            {enDirty && (
                              <Button
                                size="sm"
                                onClick={() => saveOne(row, 'en')}
                                disabled={saving[`${row.key}-en`]}
                                data-testid={`translation-save-en-${row.key}`}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex gap-1">
                            <Input
                              value={plVal}
                              onChange={(e) => setDraft(row.key, 'pl', e.target.value)}
                              className={`text-sm ${plDirty ? 'border-amber-500' : ''} ${row.pl_override ? 'bg-emerald-50' : ''}`}
                              data-testid={`translation-pl-${row.key}`}
                            />
                            {plDirty && (
                              <Button
                                size="sm"
                                onClick={() => saveOne(row, 'pl')}
                                disabled={saving[`${row.key}-pl`]}
                                data-testid={`translation-save-pl-${row.key}`}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <div className="flex justify-end gap-1">
                            {row.en_override && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => resetOne(row, 'en')}
                                disabled={saving[`${row.key}-en`]}
                                title={t('translationsEditor.revertEn')}
                                data-testid={`translation-reset-en-${row.key}`}
                              >
                                <Trash2 className="h-3 w-3 text-slate-500" />
                                <span className="ml-1 text-[10px] uppercase">{t('auto.misc2.en')}</span>
                              </Button>
                            )}
                            {row.pl_override && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => resetOne(row, 'pl')}
                                disabled={saving[`${row.key}-pl`]}
                                title={t('translationsEditor.revertPl')}
                                data-testid={`translation-reset-pl-${row.key}`}
                              >
                                <Trash2 className="h-3 w-3 text-slate-500" />
                                <span className="ml-1 text-[10px] uppercase">{t('auto.misc2.pl')}</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length > 500 && (
                <div className="text-xs text-slate-500 text-center py-2 bg-slate-50 border-t">
                  {t('translationsEditor.showingFirst', { count: 500, total: filtered.length })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmResetAll} onOpenChange={setConfirmResetAll}>
        <AlertDialogContent data-testid="translations-reset-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('translationsEditor.resetAll')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('translationsEditor.resetAllConfirm', { count: overriddenCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="translations-reset-all-cancel">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={resetAll} data-testid="translations-reset-all-confirm">
              {t('translationsEditor.resetAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TranslationsSettings;
